// offlineQueue.js - Complete Implementation
const DB_NAME = 'offline-queue-final';
const STORE_NAME = 'pending-requests';
const VERSION = 1;
const MAX_RETRIES = 3;
const REQUEST_TTL = 24 * 60 * 60 * 1000; // 24 hours
const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_QUEUE_ITEMS = 1000;

// Database instance cache
let dbInstance = null;

// Native IndexedDB wrapper
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) return resolve(dbInstance);
    
    const request = indexedDB.open(DB_NAME, VERSION);

    request.onerror = () => {
      console.error('Database error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true
        });
        
        // Create indexes
        store.createIndex('by_status', 'status');
        store.createIndex('by_priority', 'priority');
        store.createIndex('by_timestamp', 'timestamp');
        store.createIndex('by_retries', 'retries');
      }
    };

    request.onblocked = () => {
      console.warn('Database upgrade blocked');
    };
  });
};

// Safe database operation wrapper
const withDB = async (operation) => {
  let retries = 3;
  
  while (retries-- > 0) {
    try {
      const db = await openDB();
      return await operation(db);
    } catch (error) {
      if (error.name === 'InvalidStateError' && retries > 0) {
        console.warn('DB locked, retrying...');
        await new Promise(resolve => setTimeout(resolve, 100 * (3 - retries)));
        continue;
      }
      throw error;
    }
  }
};

// Transaction helper
const createTransaction = (db, mode = 'readonly') => {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, mode);
    tx.oncomplete = () => resolve();
    tx.onerror = (event) => {
      console.error('Transaction error:', event.target.error);
    };
    resolve(tx);
  });
};

// Queue Operations
// Modify addToQueue to include entity tracking
export const addToQueue = async (request, entityType, entityId) => {
  if (!('indexedDB' in window)) {
    console.warn('IndexedDB not available - falling back to memory');
    return { ...request, id: Date.now(), status: 'memory-fallback' };
  }

  return withDB(async (db) => {
    const tx = await createTransaction(db, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    // Clean up old items if queue is too large
    const count = await new Promise(resolve => {
      const countReq = store.count();
      countReq.onsuccess = () => resolve(countReq.result);
      countReq.onerror = () => resolve(0);
    });

    if (count >= MAX_QUEUE_ITEMS) {
      await new Promise(resolve => {
        const clearReq = store.clear();
        clearReq.onsuccess = resolve;
        clearReq.onerror = resolve;
      });
    }

    const item = {
  ...request,
  // Ensure entityType exists at both top level and in metadata
  entityType: request.entityType || entityType,
  metadata: {
    // Preserve existing metadata if provided
    ...(request.metadata || {}),
    // Ensure these critical fields are always set correctly
    entityType: request.entityType || entityType,
    entityId: entityId || request.metadata?.entityId,
    tempId: request.metadata?.tempId || `temp_${Date.now()}`,
    // Add timestamp if not already present
    timestamp: request.metadata?.timestamp || new Date().toISOString()
  },
  timestamp: Date.now(),
  expiresAt: Date.now() + REQUEST_TTL,
  lastAttempt: null,
  retries: 0,
  status: 'pending',
  priority: request.priority || (request.method === 'POST' ? 1 : 0)
};
    return new Promise((resolve, reject) => {
      const request = store.add(item);
      request.onsuccess = () => {
        // Notify other tabs about the new queue item
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel('offline-queue');
          channel.postMessage({ type: 'QUEUE_UPDATED' });
          channel.close();
        }
        resolve({ ...item, id: request.result });
      };
      request.onerror = () => reject(request.error);
    });
  });
};

export const processQueue = async () => {
  if (!('indexedDB' in window)) return;

  return withDB(async (db) => {
    const tx = await createTransaction(db, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('by_status');

    // Get pending requests sorted by priority (highest first)
    const requests = await new Promise((resolve) => {
      const request = index.getAll(IDBKeyRange.only('pending'));
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    // Process each request
    for (const req of requests.sort((a, b) => b.priority - a.priority)) {
      try {
        // Skip expired requests
        if (req.expiresAt && req.expiresAt < Date.now()) {
          await new Promise((resolve) => {
            const deleteReq = store.delete(req.id);
            deleteReq.onsuccess = resolve;
            deleteReq.onerror = resolve;
          });
          continue;
        }

        const response = await fetch(req.url, {
          method: req.method,
          headers: req.headers || { 'Content-Type': 'application/json' },
          body: req.method !== 'GET' ? JSON.stringify(req.data) : null
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        // Remove on success
        await new Promise((resolve) => {
          const deleteReq = store.delete(req.id);
          deleteReq.onsuccess = resolve;
          deleteReq.onerror = resolve;
        });
      } catch (error) {
        console.error(`Sync failed for ${req.url}:`, error);
        
        // Update retry status
        const updated = {
          ...req,
          lastAttempt: Date.now(),
          retries: req.retries + 1,
          status: req.retries >= MAX_RETRIES ? 'failed' : 'pending'
        };

        await new Promise((resolve) => {
          const putReq = store.put(updated);
          putReq.onsuccess = resolve;
          putReq.onerror = resolve;
        });
      }
    }
  });
};

// Utility Functions
export const getQueueStats = async () => {
  if (!('indexedDB' in window)) {
    return { total: 0, pending: 0, failed: 0 };
  }

  return withDB(async (db) => {
    const tx = await createTransaction(db);
    const store = tx.objectStore(STORE_NAME);
    
    const all = await new Promise((resolve) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    return {
      total: all.length,
      pending: all.filter(req => req.status === 'pending').length,
      failed: all.filter(req => req.status === 'failed').length,
      oldestPending: all.find(req => req.status === 'pending')?.timestamp || null
    };
  });
};

export const clearQueue = async () => {
  if (!('indexedDB' in window)) return;

  return withDB(async (db) => {
    const tx = await createTransaction(db, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    return new Promise((resolve) => {
      const request = store.clear();
      request.onsuccess = resolve;
      request.onerror = resolve;
    });
  });
};

// Initialize automatic sync on network recovery
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    processQueue().catch(console.error);
  });
  
  // Optional: Periodic sync every 5 minutes
  setInterval(() => {
    if (navigator.onLine) processQueue().catch(console.error);
  }, 300000);
}

// Add this to your exports
export const hasPendingRequests = async () => {
  const stats = await getQueueStats();
  return stats.pending > 0;
};